"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "motion/react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import Hls from "hls.js";

const STREAM_URL =
  "https://gnxrddsvzynszstsraer.supabase.co/storage/v1/object/public/Tavlo/thumbnails/gemini_generated_video_A6D1BBE1.mov";

function AnimatedHero() {
  const [titleNumber, setTitleNumber] = useState(0);
  const { isSignedIn } = useAuth();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const titles = useMemo(
    () => ["reusable", "searchable", "summarized", "unified"],
    []
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (titleNumber === titles.length - 1) {
        setTitleNumber(0);
      } else {
        setTitleNumber(titleNumber + 1);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [titleNumber, titles]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = STREAM_URL;
      return;
    }

    if (!Hls.isSupported()) {
      video.src = STREAM_URL;
      return;
    }

    const hls = new Hls({
      enableWorker: true,
      lowLatencyMode: true,
    });

    hls.loadSource(STREAM_URL);
    hls.attachMedia(video);

    return () => {
      hls.destroy();
    };
  }, []);

  const scrollToFeatures = () => {
    const element = document.getElementById("how-it-works");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Fixed video background that covers entire page */}
      <div className="fixed inset-0 w-screen h-screen z-[1]">
        <motion.div
          className="relative pointer-events-none w-full h-full"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto max-w-none object-cover mix-blend-screen"
              aria-label="Streaming visual background"
            />
            <div className="absolute inset-0 pointer-events-none z-10 bg-black/55 backdrop-blur-[1px]" />
            <div className="absolute inset-0 pointer-events-none z-[11] bg-[radial-gradient(circle_at_18%_28%,rgba(0,0,0,0.7),rgba(0,0,0,0.35)_50%,rgba(0,0,0,0.75)_85%)]" />
            <div className="absolute inset-0 pointer-events-none z-[12] bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.05)_35%,rgba(0,0,0,0.6)_78%,rgba(0,0,0,0.92)_100%)]" />
            <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none z-[13] bg-gradient-to-b from-transparent to-black/90" />
          </div>
        </motion.div>
      </div>

      <div className="relative z-20 w-full">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center gap-4 sm:gap-6 max-w-4xl mx-auto">
            <div className="hidden sm:inline-flex items-center gap-2 badge-brand mb-0">
              <span className="w-2 h-2 rounded-full bg-brand-1 animate-pulse-glow" />
              Build your social media second brain
            </div>

            <div className="flex gap-2 sm:gap-3 flex-col w-full">
              <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tighter font-bold text-text-primary leading-[1.15] sm:leading-[1.1]">
                <span className="block">Your social media</span>
                <span className="relative flex w-full justify-center overflow-hidden h-[1.3em] sm:h-[1.2em]">
                  {titles.map((title, index) => (
                    <motion.span
                      key={index}
                      className="absolute font-bold bg-gradient-to-r from-brand-1 via-brand-3 to-brand-2 bg-clip-text text-transparent"
                      initial={{ opacity: 0, y: 50 }}
                      transition={{ type: "spring", stiffness: 50 }}
                      animate={
                        titleNumber === index
                          ? {
                              y: 0,
                              opacity: 1,
                            }
                          : {
                              y: titleNumber > index ? -50 : 50,
                              opacity: 0,
                            }
                      }
                    >
                      {title}
                    </motion.span>
                  ))}
                </span>
              </h1>

              <p className="text-base sm:text-lg md:text-xl leading-relaxed tracking-tight text-text-secondary max-w-xl mx-auto">
                The all-in-one AI bookmark manager for X (Twitter), LinkedIn,
                Instagram, and more. Save, organize, and summarize the best of the internet in one searchable library.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-center gap-2 w-full sm:w-auto">
              {mounted && isSignedIn ? (
                <Link href="/today" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto gap-2 bg-gradient-to-r from-brand-1 to-brand-2 text-white border-0 hover:shadow-lg hover:shadow-brand-1/20 rounded-full h-10 sm:h-11 px-6 sm:px-7 text-sm"
                  >
                    Go to App <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              ) : (
                <Link href="/sign-in" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto gap-2 bg-gradient-to-r from-brand-1 to-brand-2 text-white border-0 hover:shadow-lg hover:shadow-brand-1/20 rounded-full h-10 sm:h-11 px-6 sm:px-7 text-sm"
                  >
                    Try it today for free <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              )}
              <Button
                size="lg"
                variant="ghost"
                className="hidden sm:inline-flex w-full sm:w-auto gap-2 text-text-secondary hover:text-brand-1 hover:bg-brand-1-light rounded-full h-11 px-7 text-sm"
                onClick={scrollToFeatures}
              >
                See how it works <ArrowDown className="w-4 h-4" />
              </Button>
              <button
                type="button"
                onClick={scrollToFeatures}
                className="sm:hidden inline-flex items-center justify-center gap-2 text-sm text-text-secondary hover:text-brand-1"
              >
                See how it works <ArrowDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { AnimatedHero };
